import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon } from '@/components/foundations';

export type DetailDrawerProps = {
  title: ReactNode;
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
};

/**
 * Painel lateral de detalhes (`aside.drawer`): ao lado no desktop, embaixo no mobile (CSS do pacote).
 * Fecha com Escape ou no X e devolve o foco para o último elemento focado fora dele (a linha da tabela).
 * Use dentro de `DrawerLayout`.
 */
export function DetailDrawer({ title, open, onClose, children }: DetailDrawerProps) {
  const titleId = useId();
  const ref = useRef<HTMLElement>(null);
  const lastOutside = useRef<HTMLElement | null>(null);
  const close = () => {
    const back = lastOutside.current;
    onClose();
    // Depois do commit (o botão com foco some do DOM e o Chrome joga o foco no body).
    requestAnimationFrame(() => {
      if (back?.isConnected) back.focus();
    });
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  // Guarda o último foco fora do painel a cada render (eventos de foco nem sempre disparam em iframe sem foco).
  useEffect(() => {
    const a = document.activeElement as HTMLElement | null;
    if (a && a !== document.body && !ref.current?.contains(a)) lastOutside.current = a;
  });

  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const t = e.target as HTMLElement;
      if (!ref.current?.contains(t)) lastOutside.current = t;
    };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <aside className="drawer" aria-live="polite" aria-labelledby={open ? titleId : undefined} ref={ref}>
      {open && (
        <>
          <div className="drawer-head">
            <h2 id={titleId}>{title}</h2>
            <button type="button" aria-label="Fechar" onClick={close}><Icon name="x" /></button>
          </div>
          {children}
        </>
      )}
    </aside>
  );
}

/** Tela com tabela + painel de detalhes (`.tx-wrap`); sem painel aberto, a tabela ocupa tudo (`closed`). */
export function DrawerLayout({ open, children, drawer }: { open: boolean; children: ReactNode; drawer: ReactNode }) {
  return (
    <div className={`tx-wrap${open ? '' : ' closed'}`}>
      <main className="tx-main">{children}</main>
      {drawer}
    </div>
  );
}
